import { useMemo } from 'react'

import { CircleQuestionMark } from 'lucide-react'
import { NavLink, useLocation } from 'react-router'

import { AppSidebar, useActions, useTypedMatches } from '@fortem-monorepo/react-spa'

import { useUserContext } from '@/index'
import { FortemRouteHandle } from '@/router'

import { type GlobalSidebarMenuItem as GSidebarMenuItem } from '../GlobalSidebar/GlobalSidebar.types'
import { GlobalSidebarUser } from '../GlobalSidebar/GlobalSidebarUser'

export interface GlobalSidebarProps extends React.PropsWithChildren {
  headerMenu?: GSidebarMenuItem<any>[]
  mainMenus?: GSidebarMenuItem<any>[][]
  footerMenu?: GSidebarMenuItem<any>[]
  companyAvatarSrc?: string
}

export const GlobalSidebar: React.FC<GlobalSidebarProps> = ({
  headerMenu = [],
  mainMenus = [],
  footerMenu = [],
  companyAvatarSrc = '/images/cropped-favicon-32x32.png',
  children,
}) => {
  const userContext = useUserContext()
  const { user } = userContext
  const { match } = useTypedMatches<FortemRouteHandle>()
  const { pathname } = useLocation()
  const { filterNested } = useActions()

  const docLink = match?.handle?.docLink
  const title = match?.handle?.title

  const footerMenuWithHelpButton: GSidebarMenuItem[] = [
    ...(docLink && title
      ? ([
          {
            type: 'link',
            label: `Docs for ${title}`,
            icon: CircleQuestionMark,
            link: docLink,
            props: { target: '_blank' },
          },
        ] satisfies GSidebarMenuItem[])
      : []),
    ...footerMenu,
  ]

  const [headerMenuFiltered, mainMenusFiltered, footerMenuFiltered] = useMemo(() => {
    const _headerMenu = filterNested(headerMenu)
    const _mainMenus = mainMenus.map((menu) => filterNested(menu)).filter((menu) => menu.length)
    const _footerMenu = filterNested(footerMenuWithHelpButton)

    return [_headerMenu, _mainMenus, _footerMenu]
  }, [headerMenu, mainMenus, footerMenu, user.roles, user.permissions, pathname])

  return (
    <AppSidebar
      headerMenu={headerMenuFiltered}
      mainMenus={mainMenusFiltered}
      footerMenu={footerMenuFiltered}
      companyAvatarSrc={companyAvatarSrc}
      companyAvatarAlt="Fortem Capital Logo"
      executeAction={(item) => item.action({ userContext })}
      isActiveLink={(item) => (item.link === '/' ? pathname === '/' : pathname.includes(item.link))}
      isDropdownLinkActive={(item) => pathname.includes(item.link) && pathname.endsWith(item.link)}
      renderLink={(item, children) => (
        <NavLink to={item.link} {...item.props}>
          {children}
        </NavLink>
      )}
      userSlot={<GlobalSidebarUser />}
    >
      {children}
    </AppSidebar>
  )
}
